/**
 * Investigation template form component (Module 24) — CoordioCore form-page-pattern.
 *
 * Layout: PageFormLayout (ShellHeader + SectionSideNav grid + panel sections)
 * Fields: @coordio/ui FormField (label + ⓘ tooltip + error + required asterisk)
 * Cards:  @coordio/ui FormSection (title + description + bordered body)
 * Toggle: @coordio/ui FormChoiceField (label + Switch in horizontal row)
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText, ListOrdered } from 'lucide-react';

import { FormField, Input, Switch, Textarea } from '@coordio/ui';
import { FormSection } from '@coordio/ui/layout';

import { PageFormLayout } from '../layouts/PageFormLayout';
import { investigationService } from '../../services/investigationService';
import { useNotificationStore } from '../../store/notificationStore';
import { ROUTES } from '../../utils/routes';
import type {
  CreateInvestigationTemplateRequest,
  UpdateInvestigationTemplateRequest,
  InvestigationTemplateDto,
} from '../../types/investigation';

export const InvestigationTemplateForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { showNotification } = useNotificationStore();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [stepsJson, setStepsJson] = useState('');
  const [defaultPromptsJson, setDefaultPromptsJson] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isSystemTemplate, setIsSystemTemplate] = useState(false);

  useEffect(() => {
    if (isEdit && id) {
      loadTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadTemplate = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const all = await investigationService.getInvestigationTemplates(false);
      const t = all.find((x) => x.id === id);
      if (!t) {
        setError('Template not found');
        return;
      }
      hydrateFromDto(t);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to load template';
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const hydrateFromDto = (t: InvestigationTemplateDto) => {
    setName(t.name);
    setDescription(t.description || '');
    setStepsJson(t.stepsJson || '');
    setDefaultPromptsJson(t.defaultPromptsJson || '');
    setIsActive(t.isActive);
    setIsSystemTemplate(t.isSystemTemplate);
  };

  const handleSubmit = async () => {
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    try {
      if (isEdit && id) {
        const request: UpdateInvestigationTemplateRequest = {
          name: name.trim(),
          description: description.trim() || undefined,
          stepsJson: stepsJson.trim() || undefined,
          defaultPromptsJson: defaultPromptsJson.trim() || undefined,
          isActive,
        };
        await investigationService.updateInvestigationTemplate(id, request);
        showNotification('Template updated successfully', 'success');
      } else {
        const request: CreateInvestigationTemplateRequest = {
          name: name.trim(),
          description: description.trim() || undefined,
          stepsJson: stepsJson.trim() || undefined,
          defaultPromptsJson: defaultPromptsJson.trim() || undefined,
          isActive,
        };
        await investigationService.createInvestigationTemplate(request);
        showNotification('Template created successfully', 'success');
      }

      navigate(ROUTES.INVESTIGATION_TEMPLATES);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to save template';
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageFormLayout
      title={isEdit ? 'Edit Template' : 'Create Template'}
      subtitle="Define reusable investigation templates with steps and default prompts."
      data-testid="investigation-template-form-page"
      loading={loading}
      error={error ?? undefined}
      onErrorDismiss={() => setError(null)}
      backTo={{ label: 'Back to List', href: ROUTES.INVESTIGATION_TEMPLATES }}
      actions={{
        onSave: handleSubmit,
        onCancel: () => navigate(ROUTES.INVESTIGATION_TEMPLATES),
        saveLabel: isEdit ? 'Update' : 'Create',
        saving,
      }}
      navGroups={[
        {
          title: 'Profile',
          items: [
            { id: 'details', label: 'Details', icon: <FileText size={14} /> },
          ],
        },
        {
          title: 'Configuration',
          items: [
            { id: 'steps', label: 'Steps', icon: <ListOrdered size={14} /> },
          ],
        },
      ]}
      sections={[
        {
          id: 'details',
          title: 'Details',
          description: 'Name, description, and activation status.',
          icon: <FileText size={16} />,
          content: (
            <FormSection
              title="Template identity"
              description="Provide a name and description for the investigation template."
            >
              <FormField
                label="Name"
                required
                description="Unique name for this investigation template."
                data-testid="investigation-template-name"
              >
                <Input
                  className="w-full"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="investigation-template-name"
                />
              </FormField>

              <div className="mt-5">
                <FormField label="Description" description="Optional details about what this template covers.">
                  <Textarea
                    className="w-full"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    data-testid="investigation-template-description"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch
                        checked={isActive}
                        onCheckedChange={setIsActive}
                        data-testid="investigation-template-isActive"
                      />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">Active</span>
                    </div>
                  </div>
                </div>
                {isSystemTemplate && (
                  <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                    This is a system template. Deletion is blocked by the backend.
                  </p>
                )}
              </div>
            </FormSection>
          ),
        },
        {
          id: 'steps',
          title: 'Steps',
          description: 'Template steps and default prompts as JSON payloads.',
          icon: <ListOrdered size={16} />,
          content: (
            <FormSection
              title="JSON payloads"
              description="Define the investigation steps and optional default prompts."
            >
              <FormField label="Steps JSON" description="JSON array describing each investigation step in order.">
                <Textarea
                  className="w-full"
                  value={stepsJson}
                  onChange={(e) => setStepsJson(e.target.value)}
                  rows={6}
                  placeholder='[{"stepName":"Collect logs","description":"...","stepType":0,"order":1}]'
                  data-testid="investigation-template-stepsJson"
                />
              </FormField>

              <div className="mt-5">
                <FormField label="Default Prompts JSON" description="JSON object with default prompt text for each step type.">
                  <Textarea
                    className="w-full"
                    value={defaultPromptsJson}
                    onChange={(e) => setDefaultPromptsJson(e.target.value)}
                    rows={6}
                    placeholder='{"DataCollection":"Prompt...","Analysis":"Prompt..."}'
                    data-testid="investigation-template-defaultPromptsJson"
                  />
                </FormField>
              </div>
            </FormSection>
          ),
        },
      ]}
    />
  );
};
